<template>
  <div class="recaptcha-wrapper">
    <!-- Show when not verified -->
    <div v-if="!isVerified && !hideWhenNotVerified">
      <!-- reCAPTCHA v2 (shown when fallback is needed) -->
      <div v-if="showV2" class="recaptcha-v2-container">
        <vue-recaptcha
          v-if="v2SiteKey"
          ref="v2Recaptcha"
          :sitekey="v2SiteKey"
          :size="v2Size"
          @verify="onV2Verify"
          @expired="onV2Expired"
          @error="onV2Error"
        />
      </div>
      <!-- Slot for content when not verified -->
      <slot name="not-verified"></slot>
    </div>
    
    <!-- Show when verified (unless hideWhenVerified is true) -->
    <div v-if="isVerified && !hideWhenVerified">
      <!-- Slot for content when verified -->
      <slot name="verified" :token="currentToken"></slot>
    </div>
    
    <!-- Default slot (always shown, useful for wrapping) -->
    <slot></slot>
  </div>
</template>

<script>
import {VueRecaptcha} from 'vue-recaptcha'

export default {
  name: 'ReCaptchaWithFallback',
  
  components: {
    VueRecaptcha
  },
  
  props: {
    // reCAPTCHA v3 site key
    // Default: 6LfkjkcrAAAAAK_ib6IZVKJe9N_frjD5cARJC_7k (from project)
    v3SiteKey: {
      type: String,
      required: false,
      default: process.env.VUE_APP_RECAPTCHA_V3_SITE_KEY || '6LfkjkcrAAAAAK_ib6IZVKJe9N_frjD5cARJC_7k'
    },
    // reCAPTCHA v2 site key (for fallback)
    // Default: 6LcAxKMhAAAAAKfZiweyJWsn89qmKB8M-ZeFFJpT (from RegisterTest.vue)
    v2SiteKey: {
      type: String,
      required: false,
      default: process.env.VUE_APP_RECAPTCHA_V2_SITE_KEY || '6LcAxKMhAAAAAKfZiweyJWsn89qmKB8M-ZeFFJpT'
    },
    // Action name for v3 (default: 'submit')
    action: {
      type: String,
      default: 'submit'
    },
    // Size for v2 ('normal', 'compact', 'invisible')
    v2Size: {
      type: String,
      default: 'normal'
    },
    // Auto-load v3 script on mount
    autoLoad: {
      type: Boolean,
      default: true
    },
    // Force use v2 (skip v3 attempt)
    forceV2: {
      type: Boolean,
      default: false
    },
    // Auto-execute reCAPTCHA when component is ready
    autoExecute: {
      type: Boolean,
      default: false
    },
    // Hide component when not verified (useful for showing submit button only when verified)
    hideWhenNotVerified: {
      type: Boolean,
      default: false
    },
    // Auto-hide when verified (useful for hiding captcha when verified)
    hideWhenVerified: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showV2: false,
      v3ScriptLoaded: false,
      v3Token: null,
      v2Token: null,
      isVerified: false,
      currentToken: null,
      isExecuting: false
    }
  },

  computed: {
    /**
     * Returns the current verification token (v3 or v2)
     */
    token() {
      return this.currentToken
    },
    
    /**
     * Returns whether reCAPTCHA is verified
     */
    verified() {
      return this.isVerified
    }
  },

  watch: {
    // Watch for when component becomes visible (for v-if scenarios)
    '$attrs': {
      handler() {
        if (this.autoExecute && !this.isVerified && !this.isExecuting) {
          this.$nextTick(() => {
            this.autoExecuteRecaptcha()
          })
        }
      },
      immediate: false
    }
  },

  mounted() {
    if (this.autoLoad && !this.forceV2) {
      this.loadV3Script()
    } else if (this.forceV2) {
      this.showV2 = true
    }
    
    // Auto-execute if enabled
    if (this.autoExecute) {
      this.$nextTick(() => {
        this.autoExecuteRecaptcha()
      })
    }
  },

  methods: {
    /**
     * Load reCAPTCHA v3 script
     */
    loadV3Script() {
      if (this.v3ScriptLoaded || !this.v3SiteKey) {
        return Promise.resolve()
      }

      // Check if script already exists
      const existingScript = document.querySelector(
        `script[src*="recaptcha/api.js?render=${this.v3SiteKey}"]`
      )

      if (existingScript) {
        // Script exists, wait for grecaptcha to be ready
        return this.waitForRecaptcha().then(() => {
          this.v3ScriptLoaded = true
        })
      }

      // Create and inject script
      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=${this.v3SiteKey}`
        script.async = true
        script.defer = true
        script.onload = () => {
          this.v3ScriptLoaded = true
          // Wait for grecaptcha to be available
          this.waitForRecaptcha().then(resolve).catch(reject)
        }
        script.onerror = () => {
          reject(new Error('Failed to load reCAPTCHA v3 script'))
        }
        document.head.appendChild(script)
      })
    },

    /**
     * Execute reCAPTCHA v3
     * @returns {Promise<string>} Token string
     */
    async executeV3() {
      if (this.forceV2 || this.showV2) {
        throw new Error('V2 is active, cannot execute V3')
      }

      // Load script if not loaded
      if (!this.v3ScriptLoaded) {
        try {
          await this.loadV3Script()
        } catch (error) {
          console.error('Failed to load reCAPTCHA v3 script:', error)
          throw error
        }
      }

      // Ensure grecaptcha is available
      if (!window.grecaptcha) {
        await this.waitForRecaptcha()
      }

      return new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
          reject(new Error('reCAPTCHA v3 not loaded'))
          return
        }

        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(this.v3SiteKey, { action: this.action })
            .then((token) => {
              this.v3Token = token
              this.currentToken = token
              this.isVerified = true
              this.$emit('verified', {
                token,
                version: 'v3'
              })
              this.$emit('update:verified', true)
              this.$emit('update:token', token)
              resolve(token)
            })
            .catch((err) => {
              console.error('reCAPTCHA v3 execute error:', err)
              this.isVerified = false
              this.currentToken = null
              this.$emit('error', {
                error: err,
                version: 'v3'
              })
              this.$emit('update:verified', false)
              this.$emit('update:token', null)
              reject(err)
            })
        })
      })
    },

    /**
     * Wait for grecaptcha to be available
     */
    waitForRecaptcha(maxAttempts = 20, interval = 100) {
      return new Promise((resolve, reject) => {
        let attempts = 0
        const checkRecaptcha = () => {
          if (window.grecaptcha) {
            resolve()
          } else if (attempts < maxAttempts) {
            attempts++
            setTimeout(checkRecaptcha, interval)
          } else {
            reject(new Error('reCAPTCHA failed to load'))
          }
        }
        checkRecaptcha()
      })
    },

    /**
     * Fallback to v2
     */
    fallbackToV2() {
      if (!this.v2SiteKey) {
        this.$emit('error', {
          error: new Error('V2 fallback requested but no v2SiteKey provided'),
          version: 'v2'
        })
        return
      }
      this.showV2 = true
      this.$emit('fallback', { version: 'v2' })
    },

    /**
     * Handle v2 verification
     */
    onV2Verify(token) {
      this.v2Token = token
      this.currentToken = token
      this.isVerified = true
      this.$emit('verified', {
        token,
        version: 'v2'
      })
      this.$emit('update:verified', true)
      this.$emit('update:token', token)
    },

    /**
     * Handle v2 expiration
     */
    onV2Expired() {
      this.v2Token = null
      this.currentToken = null
      this.isVerified = false
      this.$emit('expired', { version: 'v2' })
      this.$emit('update:verified', false)
      this.$emit('update:token', null)
    },

    /**
     * Handle v2 error
     */
    onV2Error(error) {
      this.v2Token = null
      this.currentToken = null
      this.isVerified = false
      this.$emit('error', {
        error,
        version: 'v2'
      })
      this.$emit('update:verified', false)
      this.$emit('update:token', null)
    },

    /**
     * Reset captcha
     */
    reset() {
      this.v3Token = null
      this.v2Token = null
      this.currentToken = null
      this.isVerified = false
      this.isExecuting = false
      if (this.showV2 && this.$refs.v2Recaptcha) {
        if (this.$refs.v2Recaptcha.reset) {
          this.$refs.v2Recaptcha.reset()
        }
      }
      this.$emit('reset')
      this.$emit('update:verified', false)
      this.$emit('update:token', null)
    },

    /**
     * Auto-execute reCAPTCHA when component is ready
     */
    async autoExecuteRecaptcha() {
      if (this.isExecuting || this.isVerified) {
        return
      }

      this.isExecuting = true
      
      // Wait for component to be fully mounted
      let attempts = 0
      const maxAttempts = 20
      const checkReady = () => {
        if (this.$el && this.$el.offsetParent !== null) {
          // Component is visible, try to execute
          this.execute().then((result) => {
            console.log('reCAPTCHA auto-executed successfully:', result.version)
            this.isExecuting = false
          }).catch((error) => {
            console.error('reCAPTCHA auto-execution error:', error)
            this.isExecuting = false
            // If v3 fails, v2 will be shown automatically
            // User will need to complete v2 manually
          })
        } else if (attempts < maxAttempts) {
          attempts++
          setTimeout(checkReady, 100)
        } else {
          console.warn('reCAPTCHA component not ready for auto-execution')
          this.isExecuting = false
        }
      }
      checkReady()
    },

    /**
     * Execute with automatic fallback
     * This is the main method to use - it tries v3 and falls back to v2 if needed
     * @returns {Promise<{token: string, version: 'v2'|'v3'}>}
     */
    async execute() {
      // If already using v2, wait for v2 token
      if (this.showV2 || this.forceV2) {
        if (!this.v2SiteKey) {
          throw new Error('V2 is required but no v2SiteKey provided')
        }
        if (this.v2Token) {
          return { token: this.v2Token, version: 'v2' }
        }
        // Wait for user to complete v2
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('V2 verification timeout'))
          }, 60000)

          const unwatch = this.$watch('v2Token', (token) => {
            if (token) {
              clearTimeout(timeout)
              unwatch()
              resolve({ token, version: 'v2' })
            }
          })
        })
      }

      // Try v3 first
      try {
        const token = await this.executeV3()
        return { token, version: 'v3' }
      } catch (error) {
        // If v3 fails and we have v2, fallback
        if (this.v2SiteKey) {
          this.fallbackToV2()
          // Wait for v2 verification
          return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('V2 verification timeout after V3 fallback'))
            }, 60000)

            const unwatch = this.$watch('v2Token', (token) => {
              if (token) {
                clearTimeout(timeout)
                unwatch()
                resolve({ token, version: 'v2' })
              }
            })
          })
        } else {
          throw error
        }
      }
    },

    /**
     * Get the current token (if verified)
     * @returns {string|null} The current token or null if not verified
     */
    getToken() {
      return this.currentToken
    },

    /**
     * Check if reCAPTCHA is verified
     * @returns {boolean} True if verified, false otherwise
     */
    isCaptchaVerified() {
      return this.isVerified
    }
  }
}
</script>

<style scoped>
.recaptcha-wrapper {
  display: inline-block;
}

.recaptcha-v2-container {
  display: inline-block;
}
</style>

